import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, RotateCcw, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// AmendVisitOutcomeDialog — PR-F of the Multi-Visit Cascade series.
//
// Lets a doctor or admin retroactively flip a sub-visit's outcome from
// not_today / future_need / declined → done. Wires onto PR-E's
// dos_v2.amend_visit_outcome RPC, which:
//   * Writes an immutable audit row (visit_outcome_amendments).
//   * Unwinds any cascade migration that fired off the prior outcome —
//     restoring the sub-visit's appointment_id to the appt where it
//     actually got done. Skips rows the receptionist has already
//     rebooked since the cascade fired.
//   * Re-runs Pass B for the parent item: if every sub-visit is now
//     done, flips the plan_item to Completed.
//
// Locked rule (Cameron 2026-05-07): clinical amendments do NOT touch
// money_events. Refunds remain a separate workflow.
// =====================================================================

const PRIOR_OUTCOME_LABEL = {
  not_today:    "Not today",
  future_need:  "Future need",
  declined:     "Declined",
};

export default function AmendVisitOutcomeDialog({
  open,
  onOpenChange,
  visit,           // { id, procedure_name, tooth_number, template_label, outcome, sequence }
  patientName,
  onAmended,       // (result) => void — fired after RPC succeeds
}) {
  const [reason, setReason]         = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setSubmitting(false);
  }, [open, visit?.id]);

  if (!open || !visit) return null;

  const priorLabel = PRIOR_OUTCOME_LABEL[visit.outcome] || visit.outcome || "(unknown)";
  const procLabel  = `${visit.procedure_name || "Procedure"}${visit.tooth_number ? ` · #${visit.tooth_number}` : ""}${visit.template_label ? ` · ${visit.template_label}` : ""}`;
  const validReason = reason.trim().length > 0;
  const canSubmit   = !submitting && validReason;

  const close = () => { if (!submitting) onOpenChange(false); };

  async function save() {
    if (!canSubmit) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("amend_visit_outcome", {
      p_visit_id:    visit.id,
      p_new_outcome: "done",
      p_reason:      reason.trim(),
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "Couldn't amend outcome.");
      return;
    }
    const unwound = Number(data?.unwound_rows ?? 0);
    toast.success(unwound > 0
      ? `Outcome amended. ${unwound} cascade migration${unwound === 1 ? "" : "s"} unwound.`
      : "Outcome amended.");
    onAmended?.(data);
    onOpenChange(false);
  }

  return (
    <div
      role="dialog" aria-modal="true"
      data-testid="amend-visit-outcome-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 py-3 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5 text-amber-600" strokeWidth={2.25} />
              Amend visit outcome
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5 truncate">
              {patientName ? <span className="font-medium text-slate-700">{patientName}</span> : null}
              {patientName ? " · " : null}
              {procLabel}
            </p>
          </div>
          <button type="button" onClick={close} disabled={submitting}
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700 leading-snug">
            Recorded outcome: <strong>{priorLabel}</strong>. Flipping to <strong>done</strong>
            {" "}stamps the visit complete and unwinds any cascade migration triggered by the
            prior outcome (the sub-visit returns to the appointment where it actually got done,
            unless the receptionist has already rebooked it).
            <br /><br />
            <strong>Clinical only.</strong> Money is not affected — refunds stay on the void/refund path.
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="amend-visit-reason">
              Reason <span className="text-rose-600">*required</span>
            </label>
            <textarea
              id="amend-visit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Why is this being amended? (e.g. forgot to mark prep complete on May 3)"
              data-testid="amend-visit-reason"
              className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-300 focus:border-amber-500 outline-none resize-none"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Saved with the audit log forever. Be specific.
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3 shrink-0">
          <button type="button" onClick={close} disabled={submitting}
            className="ml-auto text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded">
            Cancel
          </button>
          <button type="button"
            onClick={save}
            disabled={!canSubmit}
            data-testid="amend-visit-save"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded disabled:bg-slate-300 disabled:cursor-not-allowed">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Mark as done
          </button>
        </div>
      </div>
    </div>
  );
}
